import jsPDF from 'jspdf';

import type { Order, FulfillmentType } from '../types/@types/order';
import type { Table } from '../types/@types/table';
import { formatCurrency } from './format-currency';

export interface ReceiptLine {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
  notes?: string | null;
}

export interface ReceiptData {
  orderId?: string | number;
  orderNumber?: number | null;
  tableLabel?: string;
  fulfillment?: FulfillmentType | string | null;
  customerName?: string;
  paidAt: string;
  items: ReceiptLine[];
  subtotal: number;
  total: number;
  received?: number;
  change?: number;
}

interface BuildReceiptParams {
  order?: Order | null;
  items: ReceiptLine[];
  totals: { subtotal: number; total: number };
  table?: Table | null;
  customerName?: string;
  received?: number;
  change?: number;
  paidAt?: string;
}

export function buildReceiptData({
  order,
  items,
  totals,
  table,
  customerName,
  received,
  change,
  paidAt,
}: BuildReceiptParams): ReceiptData {
  const fulfillment = order?.fulfillmentType ?? null;
  const tableLabel =
    fulfillment === 'TAKEOUT'
      ? 'Para llevar'
      : table?.number !== undefined && table?.number !== null
        ? `Mesa ${table.number}`
        : undefined;

  return {
    orderId: order?.id,
    orderNumber: order?.orderSequence ?? null,
    tableLabel,
    fulfillment,
    customerName: customerName?.trim() || order?.customerName || undefined,
    paidAt: paidAt ?? new Date().toISOString(),
    items,
    subtotal: totals.subtotal,
    total: totals.total,
    received,
    change,
  };
}

export function downloadReceiptPdf(data: ReceiptData) {
  const doc = new jsPDF({ unit: 'mm', format: [80, 200] });
  let cursorY = 10;

  const centerText = (text: string) => {
    doc.text(text, 40, cursorY, { align: 'center' });
    cursorY += 6;
  };

  centerText('Recibo de pago');
  centerText(data.tableLabel || 'Consumo');
  if (data.orderNumber) {
    centerText(`Orden #${data.orderNumber}`);
  }

  cursorY += 2;
  doc.setFontSize(9);
  doc.text(`Fecha: ${new Date(data.paidAt).toLocaleString('es-AR')}`, 4, cursorY);
  cursorY += 5;
  if (data.customerName) {
    doc.text(`Cliente: ${data.customerName}`, 4, cursorY);
    cursorY += 5;
  }

  doc.text('Items:', 4, cursorY);
  cursorY += 5;
  data.items.forEach((item) => {
    doc.text(`${item.quantity} x ${item.name}`, 4, cursorY);
    doc.text(formatCurrency(item.total), 70, cursorY, { align: 'right' });
    cursorY += 5;
    if (item.notes) {
      doc.text(`   ${item.notes}`, 4, cursorY);
      cursorY += 4;
    }
  });

  cursorY += 2;
  doc.text(`Subtotal: ${formatCurrency(data.subtotal)}`, 4, cursorY);
  cursorY += 5;
  doc.text(`Total: ${formatCurrency(data.total)}`, 4, cursorY);
  cursorY += 5;
  if (typeof data.received === 'number') {
    doc.text(`Recibido: ${formatCurrency(data.received)}`, 4, cursorY);
    cursorY += 5;
  }
  if (typeof data.change === 'number') {
    doc.text(`Cambio: ${formatCurrency(Math.max(0, data.change))}`, 4, cursorY);
    cursorY += 5;
  }

  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return;
  }

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `recibo-${data.orderNumber ?? 'venta'}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export function triggerReceiptPrint(data: ReceiptData) {
  if (typeof window !== 'undefined' && typeof window.print === 'function') {
    window.print();
    return 'print';
  }
  downloadReceiptPdf(data);
  return 'pdf';
}

export function shouldPromptReceiptPrint(message = '¿Desea imprimir el recibo?') {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
    return false;
  }
  return window.confirm(message);
}
